import { Diagnostics } from "../../../../../services";

import { formatBytes, formatDateTime, formatDuration, formatPercent } from "./format";
import { isServiceOperation } from "./isServiceOperation";

function formatOperationName(name: string | null | undefined): string {
	if (!name) return "—";
	return isServiceOperation(name) ? `${name} (служебная)` : name;
}
/**
 * Builds a markdown report that can be pasted into chat for analysis.
 * Keep the shape stable: headings + compact tables, no UI noise.
 */
export function buildOverviewReport(input: {
	service: string;
	rangeMinutes: number;
	overview: Diagnostics.OverviewResponse | null;
	operations: Diagnostics.OperationsResponse | null;
	incidents: Diagnostics.IncidentsResponse | null;
}): string {
	const { service, rangeMinutes, overview, operations, incidents } = input;
	const lines: string[] = [
		"# Diagnostics report",
		"",
		`- generatedAt: ${new Date().toISOString()}`,
		`- service: ${service}`,
		`- rangeMinutes: ${rangeMinutes}`,
		"",
	];

	lines.push("## Pods", "");
	if (overview?.pods?.length) {
		lines.push(
			"| pod | online | heapUsed | heapDelta | rss | eventLoopMax | wsClients | subscriptions | updatedAt |",
			"| --- | --- | ---: | ---: | ---: | ---: | ---: | ---: | --- |",
		);
		for (const pod of overview.pods) {
			const cells = [
				pod.pod,
				pod.online ? "yes" : "no",
				formatBytes(pod.latest?.heapUsed),
				formatBytes(pod.heapUsedDelta),
				formatBytes(pod.latest?.rss),
				formatDuration(pod.latest?.eventLoopDelayMax),
				String(pod.latest?.extra?.wsClients ?? "—"),
				String(pod.latest?.extra?.subscriptions ?? "—"),
				pod.latest?.ts
					? new Date(pod.latest.ts).toISOString()
					: "—",
			];
			lines.push(`| ${cells.join(" | ")} |`);
		}
	} else {
		lines.push("_no pod metrics_");
	}

	lines.push("", "## Top operations", "");
	if (operations?.items?.length) {
		lines.push(
			"| operation | calls | errors | avgDuration | maxDuration |",
			"| --- | ---: | ---: | ---: | ---: |",
		);
		for (const op of operations.items.slice(0, 40)) {
			lines.push(
				`| ${op.name} | ${op.calls} | ${op.errors} | ${formatDuration(
					op.avgDuration,
				)} | ${formatDuration(op.maxDuration)} |`,
			);
		}
	} else {
		lines.push("_no operations_");
	}

	lines.push("", "## Recent incidents", "");
	if (incidents?.items?.length) {
		lines.push(
			"| id | time | service | reason | heapUsed | topOperation | pod |",
			"| --- | --- | --- | --- | ---: | --- | --- |",
		);
		for (const item of incidents.items.slice(0, 20)) {
			lines.push(
				`| ${item.id} | ${new Date(item.ts).toISOString()} | ${
					item.service
				} | ${item.reason} | ${formatBytes(
					item.heapUsed,
				)} | ${formatOperationName(item.topOperation)} | ${item.pod} |`,
			);
		}
	} else {
		lines.push("_no incidents_");
	}

	const busiestAlloc = (overview?.pods ?? [])
		.map((pod) => pod.allocations)
		.filter(Boolean)
		.sort(
			(a, b) =>
				(b?.totalSize ?? 0) - (a?.totalSize ?? 0),
		)[0] as Diagnostics.AllocationWindow | undefined;

	if (busiestAlloc) {
		lines.push("", "## Allocation window (busiest pod)", "");
		lines.push(
			`- pod: ${busiestAlloc.pod}`,
			`- ts: ${new Date(busiestAlloc.ts).toISOString()}`,
			`- durationMs: ${busiestAlloc.durationMs}`,
			`- totalSize: ${formatBytes(busiestAlloc.totalSize)}`,
			"",
			"### By module",
			"",
		);
		for (const entry of busiestAlloc.byModule.slice(0, 20)) {
			lines.push(`- ${entry.name}: ${formatBytes(entry.selfSize)}`);
		}
		lines.push("", "### By function", "");
		for (const entry of busiestAlloc.byFunction.slice(0, 20)) {
			const file = entry.file ? ` (${entry.file})` : "";
			lines.push(
				`- ${entry.name}${file}: ${formatBytes(entry.selfSize)}`,
			);
		}
	}

	lines.push("", "```json", "RAW_OVERVIEW_START");
	lines.push(
		JSON.stringify(
			{
				overview,
				operations,
				incidents,
			},
			null,
			2,
		),
	);
	lines.push("RAW_OVERVIEW_END", "```", "");

	return lines.join("\n");
}

export function buildIncidentReport(
	data: Diagnostics.IncidentResponse,
): string {
	const { incident, context } = data;
	const lines: string[] = [
		"# Incident details",
		"",
		`- generatedAt: ${new Date().toISOString()}`,
		`- id: ${incident.id}`,
		`- service: ${incident.service}`,
		`- pod: ${incident.pod}`,
		`- time: ${new Date(incident.ts).toISOString()} (${formatDateTime(
			incident.ts,
		)})`,
		`- reason: ${incident.reason}`,
		"",
		"## Memory",
		"",
		`- heapUsed: ${formatBytes(incident.memory.heapUsed)}`,
		`- heapTotal: ${formatBytes(incident.memory.heapTotal)}`,
		`- rss: ${formatBytes(incident.memory.rss)}`,
		`- external: ${formatBytes(incident.memory.external)}`,
		`- arrayBuffers: ${formatBytes(incident.memory.arrayBuffers)}`,
		`- baseline.heapUsed: ${formatBytes(incident.baseline.heapUsed)}`,
		`- baseline.rss: ${formatBytes(incident.baseline.rss)}`,
		`- eventLoopDelayMean: ${formatDuration(incident.eventLoopDelayMean)}`,
		`- eventLoopDelayMax: ${formatDuration(incident.eventLoopDelayMax)}`,
		"",
	];

	if (incident.extra && Object.keys(incident.extra).length) {
		lines.push("## Extra metrics", "");
		for (const [key, value] of Object.entries(incident.extra)) {
			lines.push(`- ${key}: ${value}`);
		}
		lines.push("");
	}

	lines.push("## Operations before incident", "");
	if (incident.operations?.length) {
		lines.push(
			"| operation | calls | baselineCalls | deviation | errors | avgDuration |",
			"| --- | ---: | ---: | ---: | ---: | ---: |",
		);
		for (const op of incident.operations.slice(0, 40)) {
			lines.push(
				`| ${formatOperationName(op.name)} | ${op.calls} | ${op.baselineCalls} | ${formatPercent(
					op.deviationPercent,
				)} | ${op.errors} | ${formatDuration(op.avgDuration)} |`,
			);
		}
	} else {
		lines.push("_no operations_");
	}

	lines.push("", "## Active subscriptions", "");
	if (incident.subscriptions?.length) {
		for (const sub of incident.subscriptions.slice(0, 40)) {
			lines.push(`- ${sub.name}: ${sub.count}`);
		}
	} else {
		lines.push("_no subscriptions_");
	}

	if (incident.cpuProfile) {
		const cpu = incident.cpuProfile;

		lines.push("", "## CPU profile before latency", "");
		lines.push(
			`- windowMs: ${cpu.durationMs}`,
			`- samplingIntervalUs: ${cpu.samplingIntervalUs}`,
			`- sampleCount: ${cpu.sampleCount}`,
			`- measuredMs: ${cpu.totalMs}`,
			"",
		);

		if (cpu.blocks?.length) {
			lines.push("### Longest uninterrupted blocks", "");
			for (const entry of cpu.blocks) {
				lines.push(
					`- ${entry.ms} ms at +${entry.offsetMs} ms: ${entry.frames.join(
						" < ",
					)}`,
				);
			}
			lines.push("");
		}

		lines.push(
			"### Hot functions",
			"",
			"| function | file | selfMs | totalMs | samples |",
			"| --- | --- | ---: | ---: | ---: |",
		);
		for (const entry of cpu.byFunction.slice(0, 20)) {
			lines.push(
				`| ${entry.name} | ${entry.file ?? "—"} | ${entry.selfMs} | ${
					entry.totalMs
				} | ${entry.samples} |`,
			);
		}

		lines.push("", "### Hot stacks", "");
		for (const entry of cpu.byStack.slice(0, 20)) {
			lines.push(`- ${entry.frames.join(" < ")}: ${entry.selfMs} ms`);
		}
	}

	if (incident.allocations) {
		lines.push("", "## Allocations", "");
		lines.push(
			`- totalSize: ${formatBytes(incident.allocations.totalSize)}`,
			`- durationMs: ${incident.allocations.durationMs}`,
			"",
			"### By module",
			"",
		);
		for (const entry of incident.allocations.byModule.slice(0, 20)) {
			lines.push(`- ${entry.name}: ${formatBytes(entry.selfSize)}`);
		}
		lines.push("", "### By function", "");
		for (const entry of incident.allocations.byFunction.slice(0, 20)) {
			const file = entry.file ? ` (${entry.file})` : "";
			lines.push(
				`- ${entry.name}${file}: ${formatBytes(entry.selfSize)}`,
			);
		}
	}

	if (context?.length) {
		lines.push("", "## Memory context (sampled points)", "");
		lines.push(
			"| ts | heapUsed | rss | eventLoopMax | wsClients | subscriptions |",
			"| --- | ---: | ---: | ---: | ---: | ---: |",
		);
		const step = Math.max(1, Math.floor(context.length / 24));
		for (let i = 0; i < context.length; i += step) {
			const point = context[i];
			lines.push(
				`| ${new Date(point.ts).toISOString()} | ${formatBytes(
					point.heapUsed,
				)} | ${formatBytes(point.rss)} | ${formatDuration(
					point.eventLoopDelayMax,
				)} | ${point.extra?.wsClients ?? "—"} | ${
					point.extra?.subscriptions ?? "—"
				} |`,
			);
		}
	}

	lines.push("", "```json", "RAW_INCIDENT_START");
	lines.push(JSON.stringify(data, null, 2));
	lines.push("RAW_INCIDENT_END", "```", "");

	return lines.join("\n");
}

export async function copyText(text: string): Promise<boolean> {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// fall through to legacy path
	}

	try {
		const area = document.createElement("textarea");
		area.value = text;
		area.setAttribute("readonly", "");
		area.style.position = "fixed";
		area.style.left = "-9999px";
		document.body.appendChild(area);
		area.select();
		const ok = document.execCommand("copy");
		document.body.removeChild(area);
		return ok;
	} catch {
		return false;
	}
}
